import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TableCell } from '@/shared/components/MaterialTableElements';
import { Button } from '@/shared/components/Button';
import MatTableHead from './components/MatTableHead';
import VideoPreviewModal from './components/video-preview-modal';
import { useCourse33Shares } from '@/services/course-33-shares';
import Course33VideoShare from '@/models/course-33-share';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { formatDate } from '@/utils/helpers';
import { darkTextDim } from '@/shared/dark-inline-styles';

const PAGE_SIZE = 20;

// Course-33 runs across 21 program days; a share carries its day (1–21).
const COURSE_33_DAYS = Array.from({ length: 21 }, (_, i) => i + 1);

const Course33Videos = () => {
    const [page, setPage] = useState(1); // 1-indexed for the API
    const [userId, setUserId] = useState('');
    const [userIdInput, setUserIdInput] = useState('');
    const [day, setDay] = useState<number | undefined>(undefined); // undefined = all days

    const {
        data: result,
        isLoading,
        isFetching,
    } = useCourse33Shares({
        page,
        limit: PAGE_SIZE,
        userId: userId || undefined,
        day,
    });
    const shares = result?.data?.shares ?? [];
    const hasMore = result?.data?.hasMore ?? false;
    // callApi never throws (admin CLAUDE.md §1 gotcha) — a 403/500/network error
    // comes back as { status: 'error', data: null }. Without this an API failure
    // would render a misleading "No videos found." (as if the user simply hasn't
    // uploaded), so surface a real error state instead.
    const loadFailed = !isLoading && result?.status === 'error';

    const [isOpen, setIsOpen] = useState(false);
    const [selectedShare, setSelectedShare] = useState<Course33VideoShare>();

    const toggle = () => setIsOpen((prev) => !prev);
    const handlePlay = (share: Course33VideoShare) => {
        setSelectedShare(share);
        setIsOpen(true);
    };

    const applyUserId = () => {
        setUserId(userIdInput.trim());
        setPage(1);
    };
    const onUserIdKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') applyUserId();
    };

    const onDayChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setDay(value === '' ? undefined : Number(value));
        setPage(1);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Course 33 Videos</h3>
                    <h3 className="page-subhead subhead">Review user-uploaded daily videos — view-only. Filter by user and by day.</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {/* Filters: user picker + day selector. Each MaterialTextField is
                                width:100%, so use the responsive grid (side-by-side on lg+,
                                stacked on small screens) rather than a flex row that would wrap. */}
                            <Row className="align-items-center">
                                <Col xs={12} lg={8}>
                                    <MaterialTextField
                                        label="Filter by User ID (press Enter)"
                                        type="text"
                                        value={userIdInput}
                                        onChange={(e) => setUserIdInput(e.target.value)}
                                        onKeyDown={onUserIdKeyDown}
                                        onBlur={applyUserId}
                                    />
                                </Col>
                                <Col xs={12} lg={4}>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Day"
                                        value={day === undefined ? '' : String(day)}
                                        onChange={onDayChange}
                                    >
                                        <MaterialFormOption value="">All days</MaterialFormOption>
                                        {COURSE_33_DAYS.map((d) => (
                                            <MaterialFormOption key={d} value={String(d)}>
                                                Day {d}
                                            </MaterialFormOption>
                                        ))}
                                    </MaterialTextField>
                                </Col>
                            </Row>

                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : loadFailed ? (
                                <div className="text-center text-danger" style={{ padding: '2rem' }}>
                                    Couldn’t load videos. Check your connection or the API and try again.
                                </div>
                            ) : (
                                <>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead />
                                            <TableBody>
                                                {shares.map((d) => (
                                                    <TableRow tabIndex={-1} key={d.id}>
                                                        <TableCell component="th" scope="row" padding="none">
                                                            {d.userId}
                                                        </TableCell>
                                                        <TableCell>{d.day}</TableCell>
                                                        <TableCell>{d.enrollmentId}</TableCell>
                                                        <TableCell>{d.caption?.trim() ? d.caption : '—'}</TableCell>
                                                        <TableCell>{formatDate(d.createdAt)}</TableCell>
                                                        <TableCell>
                                                            <Button variant="primary" size="sm" style={{ margin: 0 }} onClick={() => handlePlay(d)}>
                                                                Play
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {shares.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} className="text-center">
                                                            No videos found.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>

                                    {/* The API returns `hasMore` (no total), so use Prev/Next
                                        controls rather than MUI TablePagination (which needs a count). */}
                                    <div className="d-flex align-items-center justify-content-end gap-2 mt-3">
                                        <span style={{ fontSize: 13, color: darkTextDim }}>Page {page}</span>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            style={{ margin: 0 }}
                                            disabled={page <= 1 || isFetching}
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        >
                                            Previous
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            style={{ margin: 0 }}
                                            disabled={!hasMore || isFetching}
                                            onClick={() => setPage((p) => p + 1)}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <VideoPreviewModal isOpen={isOpen} share={selectedShare} toggle={toggle} />
        </Container>
    );
};

export default Course33Videos;
